import { useEffect, useRef, useState } from "react";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import type {
  AgencySubscription,
  RentedSubscription,
  Subscription,
  SubscriptionType,
} from "common/types";

type Step =
  | "manage"
  | "success"
  | "successLarge"
  | "paymentMethod"
  | "unassignLicenses"
  | "cancel";

export default function useManageSubscriptionSteps(
  initialState:
    | {
        subscription: AgencySubscription | Subscription | RentedSubscription;
        seats: number;
      }
    | SubscriptionType,
  isOpen: boolean,
) {
  const [manageSubscriptionStep, setManageSubscriptionStep] =
    useState<Step>("manage");

  const { rental_subscriptions } = useSelectedWorkspaceContext();

  const subscription =
    typeof initialState === "string" ? undefined : initialState.subscription;

  const subscriptionType =
    typeof initialState === "string"
      ? initialState
      : initialState.subscription.type;

  // Large success screen for new subs or reactivations; Avatar only on first purchase
  const isNewOrReactivationRef = useRef<boolean>();

  useEffect(() => {
    if (!isOpen) {
      isNewOrReactivationRef.current = undefined;
      return;
    }

    if (isNewOrReactivationRef.current !== undefined) {
      return;
    }

    let isNewOrReactivation =
      !subscription || subscription.status === "cancelled";

    if (
      subscriptionType === "rental" &&
      (rental_subscriptions?.length ?? 0) > 0
    ) {
      isNewOrReactivation = false;
    }

    isNewOrReactivationRef.current = isNewOrReactivation;
  }, [
    isOpen,
    initialState,
    rental_subscriptions,
    subscription,
    subscriptionType,
  ]);

  let dialogSize: "md" | "lg" | "xl" =
    manageSubscriptionStep === "manage" ||
    manageSubscriptionStep === "successLarge"
      ? "xl"
      : "md";
  if (manageSubscriptionStep === "cancel") {
    dialogSize = "lg";
  }

  function goToManageSubscriptionStep(step: Exclude<Step, "successLarge">) {
    if (step === "success") {
      setManageSubscriptionStep(
        isNewOrReactivationRef.current ? "successLarge" : "success",
      );
    } else {
      setManageSubscriptionStep(step);
    }
  }

  return {
    manageSubscriptionStep,
    goToManageSubscriptionStep,
    resetManageSubscriptionStep: () => setManageSubscriptionStep("manage"),
    dialogSize,
  } as const;
}
